import { Grid } from "@mui-wrapper";
import { FormContainer } from "@components/forms/form-container";
import { ContentLayout } from "@pages/content-layout";
import { useAppNavigation } from "@router/use-app-navigation";
import { useProjectCreationContext } from "@pages/create-project/project-creation-context";
import { isValidEmail } from "@pages/create-project/create-project-helper";
import { ProjectManagerAutocomplete } from "@pages/create-project/project-manager-autocomplete";
import { FaroCoreApiConst } from "@stellar/core-api-client";
import { StringHeader } from "@components/string-header";
import { Dropdown, Switch, Text, TextField } from "@flat-ui";
import { formatSubjectType } from "@utils/data-display";

/** Shows a project creation page */
export function CreateProjectPage(): React.JSX.Element {
  const projectCreationContext = useProjectCreationContext();
  const { navigateToProfilePage } = useAppNavigation();

  const {
    subject,
    projectName,
    setProjectName,
    projectAddress,
    setProjectAddress,
    projectArea,
    setProjectArea,
    projectAreaUnit,
    setProjectAreaUnit,
    projectManager,
    setProjectManager,
    isStandalone,
    setIsStandalone,
    setShouldShowSummary,
  } = projectCreationContext;

  function isProjectCreationDataValid(): boolean {
    return !!(
      projectName &&
      projectAddress &&
      projectArea &&
      projectArea > 0 &&
      isValidEmail(projectManager)
    );
  }

  return (
    <ContentLayout header={<StringHeader title="Create New Project" />}>
      <FormContainer
        sx={{
          width: "550px",
          ml: "110px",
          mt: "10px",
          "& .MuiFormControl-root": {
            mt: 2.5,
          },
        }}
        handleCancelClicked={() =>
          navigateToProfilePage({
            subjectType: subject.type,
            subjectId: subject.company.id,
          })
        }
        handleCreateClicked={() => setShouldShowSummary(true)}
        shouldCreateButtonBeDisabled={!isProjectCreationDataValid()}
      >
        <Text variant="heading20">Project Details</Text>
        <br />
        <Text variant="bodyL">
          This project is added to <b>{subject.group.name}</b> group in{" "}
          <b>{subject.company.name}</b> {formatSubjectType(subject.type)}
        </Text>

        <Grid container>
          {/* project name */}
          <TextField
            onTextChanged={(text) => setProjectName(text)}
            text={projectName}
            label="Project Name"
            tag="required"
            fullWidth
            controlStyle={{ my: 2 }}
          />

          {/* Project Manager */}
          <ProjectManagerAutocomplete
            projectManager={projectManager}
            members={subject.groupMembers}
            setProjectManager={setProjectManager}
          />

          {/* project address */}
          <TextField
            onTextChanged={(text) => setProjectAddress(text)}
            text={projectAddress}
            label="Project Address"
            tag="required"
            fullWidth
            controlStyle={{ mt: 2 }}
          />

          <Grid container spacing={1}>
            <Grid size={{ xs: 7 }} sx={{ mb: 2.5, mx: 0 }}>
              {/* project area */}
              <TextField
                type="number"
                label="Project Area"
                text={String(projectArea)}
                onTextChanged={(area) => setProjectArea(Number(area))}
                fullWidth
                // The dropdown next to it has a 4px gap between its label and its field, which a
                // text field does not, so the extra 4px keeps the two fields on the same line
                controlStyle={{ mt: 2.5 }}
                // Matches the height of the unit dropdown next to it
                sx={{ height: "3.5rem", boxSizing: "border-box" }}
              />
            </Grid>

            <Grid size={{ xs: 5 }} sx={{ mb: 2.5, mx: 0 }}>
              {/* product type */}
              <Dropdown
                label="Project Area Unit"
                fullWidth
                onChange={(event) => {
                  setProjectAreaUnit(event.target.value);
                }}
                value={projectAreaUnit}
                options={Object.values(FaroCoreApiConst.allAreaUnits).map(
                  (areaUnit) => ({
                    key: areaUnit,
                    label: areaUnit,
                    value: areaUnit,
                  })
                )}
                containerSx={{ mt: 2 }}
                sx={{ height: "3.5rem", boxSizing: "border-box" }}
              />
            </Grid>

            {/* standalone projects */}
            <>
              <Switch
                checked={isStandalone}
                onToggle={() => setIsStandalone(!isStandalone)}
                label="Standalone?"
              />

              <Text variant="bodyL">
                {isStandalone
                  ? "This project won't use up any project slot or sq ft of the workspace."
                  : "This project will count towards the project/sq ft limit of the workspace."}
              </Text>
            </>
          </Grid>
        </Grid>
      </FormContainer>
    </ContentLayout>
  );
}
